import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { value: "new", label: "New Lead" },
  { value: "contacted", label: "Contacted" },
  { value: "strategy_call", label: "Strategy Call" },
  { value: "proposal", label: "Proposal" },
  { value: "client", label: "Client" },
];

const inputCls =
  "w-full border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-faint/70 focus:border-amber focus:outline-none";

export default function AdminLeads() {
  const [key, setKey] = useState(sessionStorage.getItem("kps_admin_key") || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Leads — KP Strategy";
  }, []);

  useEffect(() => {
    if (!key) return;
    (async () => {
      try {
        const res = await fetch(`${API}/leads`, { headers: { "x-admin-key": key } });
        if (!res.ok) throw new Error(res.status === 401 ? "Incorrect access key." : "Could not load leads.");
        setLeads(await res.json());
        setError("");
      } catch (e) {
        setError(e.message);
        setLeads(null);
        if (e.message === "Incorrect access key.") {
          sessionStorage.removeItem("kps_admin_key");
          setKey("");
        }
      }
    })();
  }, [key]);

  const unlock = (e) => {
    e.preventDefault();
    sessionStorage.setItem("kps_admin_key", input);
    setKey(input);
  };

  const updateStage = async (id, stage) => {
    setLeads(leads.map((l) => (l.id === id ? { ...l, stage } : l)));
    try {
      await fetch(`${API}/leads/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "x-admin-key": key },
        body: JSON.stringify({ stage }),
      });
    } catch {
      /* keep UI state; next reload reconciles */
    }
  };

  if (!key || leads === null) {
    return (
      <main data-testid="admin-page" className="flex min-h-[70vh] items-center pt-[72px]">
        <div className="mx-auto w-full max-w-md px-4 py-24">
          <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
            <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
            Admin
          </p>
          <h1 className="mt-6 font-serif text-3xl font-light text-ink sm:text-4xl">Lead inbox</h1>
          <form onSubmit={unlock} className="mt-8 space-y-4" data-testid="admin-login-form">
            <input
              data-testid="admin-key-input"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Access key"
              className={inputCls}
              required
            />
            {error && (
              <p data-testid="admin-login-error" className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              className="rounded-full bg-ink px-7 py-3 text-sm font-semibold text-paper transition-colors hover:bg-amber"
            >
              View Leads
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-leads-page" className="pt-[72px]">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">Admin</p>
            <h1 className="mt-3 font-serif text-3xl font-light text-ink sm:text-4xl">
              Lead inbox <span className="text-faint">({leads.length})</span>
            </h1>
          </div>
          <button
            data-testid="admin-logout"
            onClick={() => {
              sessionStorage.removeItem("kps_admin_key");
              setKey("");
              setLeads(null);
            }}
            className="text-sm font-semibold text-body underline decoration-line underline-offset-4 hover:text-amber"
          >
            Lock
          </button>
        </div>

        {leads.length === 0 ? (
          <p data-testid="admin-empty" className="mt-12 border border-line bg-parch p-10 text-center text-body">
            No leads yet. New form submissions will appear here.
          </p>
        ) : (
          <div className="mt-10 overflow-x-auto border border-line bg-white" data-testid="admin-leads-table">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead>
                <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Organization</th>
                  <th className="px-5 py-4">Audience</th>
                  <th className="px-5 py-4">Source</th>
                  <th className="px-5 py-4">Needs</th>
                  <th className="px-5 py-4">Stage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="align-top">
                    <td className="whitespace-nowrap px-5 py-4 text-faint">
                      {new Date(l.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4 font-semibold text-ink">{l.name || "—"}</td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${l.email}`} className="text-ink underline decoration-amber/50 underline-offset-2 hover:text-amber">
                        {l.email}
                      </a>
                    </td>
                    <td className="px-5 py-4 text-body">{l.organization || "—"}</td>
                    <td className="px-5 py-4 capitalize text-body">{l.audience || "—"}</td>
                    <td className="px-5 py-4">
                      <span className="border border-line bg-parch px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-body">
                        {l.source}
                      </span>
                    </td>
                    <td className="max-w-[220px] px-5 py-4 text-body">{l.needs || "—"}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`admin-stage-select-${l.id}`}
                        value={l.stage}
                        onChange={(e) => updateStage(l.id, e.target.value)}
                        className="border border-line bg-paper px-2 py-1.5 text-xs font-semibold text-ink focus:border-amber focus:outline-none"
                      >
                        {STAGES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
